import React from 'react';
import { createHashRouter, Navigate, Outlet, useLocation } from 'react-router';

import { useAppSelector } from 'store/store';
import { usePermissions } from 'hooks/usePermissions';
import { PageLayout } from 'components/Layout/PageLayout';
import { LoginPage } from 'pages/LoginPage/LoginPage';
import { LotteryListPage } from 'pages/LotteryList/LotteryListPage';
import { DrawingsListPage } from 'pages/Drawings/DrawingsListPage';
import { DrawingCreatePage } from 'pages/Drawings/DrawingCreatePage';
import { DrawingEditPage } from 'pages/Drawings/DrawingEditPage';
import { DrawingPrizeCountPage } from 'pages/Drawings/DrawingPrizeCountPage';
import { DrawingBasesListPage } from 'pages/DrawingBases/DrawingBasesListPage';
import { DrawingBaseCreatePage } from 'pages/DrawingBases/DrawingBaseCreatePage';
import { DrawingBaseEditPage } from 'pages/DrawingBases/DrawingBaseEditPage';
import { UserListPage } from 'pages/Users/UserListPage';
import { UserCreatePage } from 'pages/Users/UserCreatePage';
import { UserEditPage } from 'pages/Users/UserEditPage';
import { GeneralPage } from 'pages/General/GeneralPage';
import { StatisticsPage } from 'pages/Statistics/StatisticsPage';
import { ChangePasswordPage } from 'pages/Profile/ChangePasswordPage';
import { ChangeEmailPage } from 'pages/Profile/ChangeEmailPage';
import { NotFoundPage } from 'pages/NotFoundPage';

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, authenticatedStatus } = useAppSelector((state) => state.auth);

  if (authenticatedStatus === 'pending') {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <Outlet />;
};

const RouteWrapper: React.FC<{ component: React.ComponentType }> = ({ component }) => {
  const location = useLocation();
  const key = location.state?.reload ? `${location.pathname}-${location.state.reload}` : location.pathname;
  const WrappedComponent = component;
  return <WrappedComponent key={key} />;
};

const LandingRoute: React.FC = () => {
  const lotteryId = useAppSelector((state) => state.auth.user?.lotteryId);
  const { isSuperAdmin, isAdmin, isRedakteur, isFreigeber } = usePermissions();

  if (isSuperAdmin()) {
    return <RouteWrapper component={LotteryListPage} />;
  }

  if (lotteryId) {
    if (isRedakteur() || isFreigeber()) {
      return <Navigate to={`/lottery/${lotteryId}/drawings`} replace />;
    }
    if (isAdmin()) {
      return <Navigate to={`/lottery/${lotteryId}/users`} replace />;
    }
  }

  return <Navigate to="/profile/change-password" replace />;
};

export const createRouter = () => {
  return createHashRouter([
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <PageLayout />,
          children: [
            {
              path: '/',
              element: <LandingRoute />,
            },
            {
              path: '/lottery/:lotteryId/drawings',
              element: <RouteWrapper component={DrawingsListPage} />,
            },
            {
              path: '/lottery/:lotteryId/drawings/create',
              element: <RouteWrapper component={DrawingCreatePage} />,
            },
            {
              path: '/lottery/:lotteryId/drawings/:drawingId/edit',
              element: <RouteWrapper component={DrawingEditPage} />,
            },
            {
              path: '/lottery/:lotteryId/drawings/:drawingId/prize-counts',
              element: <RouteWrapper component={DrawingPrizeCountPage} />,
            },
            {
              path: '/lottery/:lotteryId/drawing-bases',
              element: <RouteWrapper component={DrawingBasesListPage} />,
            },
            {
              path: '/lottery/:lotteryId/drawing-bases/create',
              element: <RouteWrapper component={DrawingBaseCreatePage} />,
            },
            {
              path: '/lottery/:lotteryId/drawing-bases/:drawingBaseId/edit',
              element: <RouteWrapper component={DrawingBaseEditPage} />,
            },
            {
              path: '/lottery/:lotteryId/users',
              element: <RouteWrapper component={UserListPage} />,
            },
            {
              path: '/lottery/:lotteryId/users/create',
              element: <RouteWrapper component={UserCreatePage} />,
            },
            {
              path: '/lottery/:lotteryId/users/:userId/edit',
              element: <RouteWrapper component={UserEditPage} />,
            },
            {
              path: '/lottery/:lotteryId/general',
              element: <RouteWrapper component={GeneralPage} />,
            },
            {
              path: '/lottery/:lotteryId/statistics',
              element: <RouteWrapper component={StatisticsPage} />,
            },
            {
              path: '/profile/change-password',
              element: <RouteWrapper component={ChangePasswordPage} />,
            },
            {
              path: '/profile/change-email',
              element: <RouteWrapper component={ChangeEmailPage} />,
            },
            {
              path: '*',
              element: <NotFoundPage />,
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/login" />,
    },
  ]);
};
